import { useEffect } from "react";
import { allianceCan } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { Icon } from "../ui/Icon.js";
import { useAlliance } from "./state.js";
import type { AllianceView } from "./types.js";

/** Buildings tab (D72): six alliance buildings with level, progress and effect; officers choose the focus. */
export function Buildings({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  const buildings = useAlliance((s) => s.buildings);
  const refreshBuildings = useAlliance((s) => s.refreshBuildings);
  const setFocus = useAlliance((s) => s.setFocus);
  useEffect(() => {
    void refreshBuildings();
  }, [refreshBuildings]);
  const officer = alliance.myRole !== null && allianceCan(alliance.myRole, "focus");
  if (!buildings) return <p className="hint" style={{ padding: 12 }}>{t("common.loading")}</p>;
  return (
    <div className="alliance-buildings" data-testid="alliance-buildings">
      <p className="hint" style={{ margin: "0 12px 8px" }}>{t("alliance.buildings.hint", { n: alliance.level })}</p>
      {buildings.map((b) => (
        <Panel key={b.id} title={<span className="row" style={{ gap: 8 }}>{pick(b.name)}{b.focused ? <span className="badge gold" data-testid="focus-badge">{t("alliance.buildings.focused")}</span> : null}</span>}>
          <div className="alliance-building" data-testid={`building-${b.id}`}>
            <span className="mono">{t("alliance.buildings.level", { n: b.level, max: b.max })}</span>
            <span className="hint">{t(`alliance.effect.${b.effect.key}`, { n: b.effect.perLevel })} · {t("alliance.buildings.total")}: {t(`alliance.effectTotal.${b.effect.key}`, { n: b.effect.total })}</span>
            {b.next !== null ? (
              <>
                <div className="alliance-progress"><span style={{ width: `${Math.min(100, Math.round((b.progress / b.next) * 100))}%` }} /></div>
                <span className="mono hint">{b.progress}/{b.next}</span>
              </>
            ) : (
              <span className="hint">{t("alliance.buildings.capped")}</span>
            )}
            {officer ? (
              <Button variant={b.focused ? "default" : "primary"} disabled={b.focused || b.next === null} onClick={() => void setFocus(b.id)} data-testid={`focus-${b.id}`}>
                <Icon name="flag" /> {b.focused ? t("alliance.buildings.focused") : t("alliance.buildings.setFocus")}
              </Button>
            ) : null}
          </div>
        </Panel>
      ))}
    </div>
  );
}
